<template>
  <div>
    <h4 class="table-page-header">
      Clients
      <el-tooltip content="Add New Client">
        <el-button type="text" @click="createClient">
          <i class="fa-solid fa-circle-plus" style="font-size: 22px"></i>
        </el-button>
      </el-tooltip>
    </h4>
    <div class="filter-section">
      <div class="filter-row">
        <div class="filter-group search-group">
          <el-input
            v-model="searchQuery"
            placeholder="Search"
            clearable
            @clear="clearSearch"
            @input="onSearch"
            class="search-input"
          >
            <i
              slot="prepend"
              class="fa fa-search"
              style="padding-left: 4px; padding-right: 4px"
            ></i>
          </el-input>
        </div>
        <div class="filter-group">
          <AIVSelect
            v-model="payerTypeFilter"
            :options="payerTypeFilterOptions"
            placeholder="Payer Type"
            @change="onPayerTypeFilterChange"
            class="compact-filter"
          />
        </div>
        <div class="filter-group">
          <AIVSelect
            v-model="officeFilter"
            :options="officeOptions"
            placeholder="Office"
            @change="onOfficeFilterChange"
            class="compact-filter"
          />
        </div>
        <div class="filter-group">
          <AIVSelect
            v-model="activeStatusFilter"
            :options="activeStatusOptions"
            placeholder="Status"
            @change="onStatusFilterChange"
            class="compact-filter"
          />
        </div>
        <div class="filter-group">
          <AIVSelect
            v-model="programFilter"
            :options="programOptions"
            placeholder="CBS Program"
            @change="onProgramFilterChange"
            class="compact-filter"
          />
        </div>
      </div>
    </div>
    <AIVTable
      :columns="clientColumns"
      :data="clients"
      :pagination="pagination"
      :item-name="'clients'"
      :row-clickable="true"
      @pageChange="fetchClients"
      @pageSizeChange="handlePageSizeChange"
      @sort-change="handleSortChange"
      @row-click="handleRowClick"
    >
      <template #cell-actions="{ row }">
        <button
          :class="row.active ? 'btn btn-sm btn-danger' : 'btn btn-sm btn-success'"
          @click.stop="$confirmAction(() => toggleClientStatus(row), row.active ? `Are you sure you want to deactivate ${row.first_name} ${row.last_name}? They will no longer be available to choose for services.` : `Are you sure you want to activate ${row.first_name} ${row.last_name}? They will be available to choose for services again.`)"
          :title="row.active ? 'Deactivate' : 'Activate'"
        >
          <i :class="row.active ? 'fa-solid fa-user-slash' : 'fa-solid fa-user-check'"></i>
        </button>
      </template>
    </AIVTable>
    <AIVDialog
      v-model="$modal.state.create_or_edit_client"
      title="Create a New Client"
      height="85vh"
    >
      <div class="client-form-container">
        <el-form
          ref="createForm"
          :model="client"
          label-position="top"
          label-width="120px"
          class="full-width-form"
        >
        <AIVRow
          :record.sync="client"
          :fields="[
            { label: 'First Name', key: 'first_name' },
            { label: 'Last Name', key: 'last_name' },
            {
              label: 'Payer Type',
              key: 'payer_type',
              type: 'select',
              options: payerTypeOptions,
              placeholder: 'Select Payer Type',
            },
          ]"
        />
        </el-form>
      </div>
      <template slot="footer">
        <div class="text-center">
          <el-button type="primary" round @click="saveClient">
            Create
          </el-button>
        </div>
      </template>
    </AIVDialog>
  </div>
</template>

<script>
import { clientsService } from "@/serviceHandlers/clientsService";
import { officesService } from "@/serviceHandlers/officesService";
import { cbsProgramsService } from "@/serviceHandlers/cbsProgramsService";

export default {
  name: "Clients",
  components: {},
  data() {
    return {
      clientColumns: [
        { key: "first_name", label: "First Name" },
        { key: "last_name", label: "Last Name" },
        { key: "payer_type", label: "Payer" },
        { key: "email", label: "Email" },
        { key: "phone", label: "Phone" },
        { key: "actions", label: "Actions", sortable: false },
      ],
      newClient: {
        first_name: "",
        last_name: "",
        payer_type: "DSPD",
      },
      searchQuery: "",
      payerTypeFilter: null,
      payerTypeFilterOptions: [
        { id: "DSPD", name: "DSPD" },
        { id: "VR", name: "VR" },
      ],
      officeFilter: null,
      officeOptions: [],
      programFilter: null,
      programOptions: [],
      activeStatusFilter: "active",
      activeStatusOptions: [
        { id: "active", name: "Active" },
        { id: "inactive", name: "Inactive" },
      ],
      sortProp: "",
      sortOrder: "",
      clients: [],
      pagination: {
        total: 0,
        perPage: 20,
        currentPage: 1,
        totalPages: 0,
      },
      payerTypeOptions: [
        { id: "DSPD", name: "DSPD" },
        { id: "VR", name: "VR" },
      ],
    };
  },
  computed: {
    client: {
      get() {
        return this.newClient;
      },
      set(val) {
        this.newClient = val;
      },
    },
  },
  async mounted() {
    await this.loadOffices();
    await this.loadPrograms();
    await this.fetchClients();
  },
  methods: {
    async fetchClients(pageNumber = 1) {
      this.$spinner.show();
      try {
        const params = new URLSearchParams();
        params.append("page", pageNumber);
        params.append("perPage", this.pagination.perPage);
        params.append("activeStatus", this.activeStatusFilter);

        if (this.payerTypeFilter) {
          params.append("payerType", this.payerTypeFilter);
        }

        if (this.officeFilter) {
          params.append("officeId", this.officeFilter);
        }

        if (this.searchQuery) {
          params.append("searchTerm", this.searchQuery);
        }

        if (this.sortProp) {
          params.append("sortField", this.sortProp);
          params.append("sortOrder", this.sortOrder);
        }

        if (this.programFilter) {
          params.append("programId", this.programFilter);
        }

        const response = await clientsService.listPaginated(params);
        this.clients = response.rows.map((client) => {
          // Email logic: VR uses best_contact_email, DSPD uses client_email
          let email = '';
          if (client.payer_type === 'VR') {
            email = client.best_contact_email || '';
          } else if (client.payer_type === 'DSPD') {
            email = client.client_email || '';
          }

          // Phone logic: VR uses best_contact_phone, DSPD uses contacts array
          let phone = '';
          if (client.payer_type === 'VR') {
            phone = client.best_contact_phone || '';
          } else if (client.payer_type === 'DSPD') {
            // Look for mobile phone first, then home phone in contacts
            if (client.contacts && client.contacts.length > 0) {
              // First try to find mobile phone
              const mobileContact = client.contacts.find(
                c => c.phone_type && c.phone_type.toLowerCase() === 'mobile' && c.phone
              );
              if (mobileContact) {
                phone = mobileContact.phone;
              } else {
                // Try alternate phone if it's mobile
                const mobileAltContact = client.contacts.find(
                  c => c.phone_alt_type && c.phone_alt_type.toLowerCase() === 'mobile' && c.phone_alt
                );
                if (mobileAltContact) {
                  phone = mobileAltContact.phone_alt;
                } else {
                  // Fall back to home phone
                  const homeContact = client.contacts.find(
                    c => c.phone_type && c.phone_type.toLowerCase() === 'home' && c.phone
                  );
                  if (homeContact) {
                    phone = homeContact.phone;
                  } else {
                    // Try alternate phone if it's home
                    const homeAltContact = client.contacts.find(
                      c => c.phone_alt_type && c.phone_alt_type.toLowerCase() === 'home' && c.phone_alt
                    );
                    if (homeAltContact) {
                      phone = homeAltContact.phone_alt;
                    }
                  }
                }
              }
            }
          }

          return {
            ...client,
            email,
            phone,
          };
        });
        this.pagination = response.pagination;
      } catch (error) {
        this.$notifyError("Could not load clients: " + error);
      } finally {
        this.$spinner.hide();
      }
    },
    onSearch() {
      this.pagination.currentPage = 1;
      this.fetchClients(1);
    },
    clearSearch() {
      this.searchQuery = "";
      this.pagination.currentPage = 1;
      this.fetchClients(1);
    },
    onStatusFilterChange() {
      this.pagination.currentPage = 1;
      this.fetchClients(1);
    },
    onPayerTypeFilterChange() {
      this.pagination.currentPage = 1;
      this.fetchClients(1);
    },
    onOfficeFilterChange() {
      this.pagination.currentPage = 1;
      this.fetchClients(1);
    },
    async loadOffices() {
      try {
        const offices = await officesService.list();
        this.officeOptions = [
          { id: "none", name: "No Office" },
          ...offices.map((o) => ({ id: o.id, name: o.name })),
        ];
      } catch (error) {
        this.$notifyError("Could not load offices: " + error);
      }
    },
    async loadPrograms() {
      try {
        const programs = await cbsProgramsService.list();
        this.programOptions = (programs || []).map((p) => ({ id: p.id, name: p.display_name || p.name }));
      } catch {
        // Programs may not be available yet
      }
    },
    onProgramFilterChange() {
      this.pagination.currentPage = 1;
      this.fetchClients(1);
    },
    handleSortChange({ prop, order }) {
      this.sortProp = prop;
      this.sortOrder = order;
      this.fetchClients(1);
    },
    handlePageSizeChange(newSize) {
      this.pagination.perPage = newSize;
      this.pagination.currentPage = 1;
      this.fetchClients(1);
    },
    getClients() {
      return clientsService
        .list()
        .then((clients) => {
          this.clients = clients.map((client) => {
            // Email logic: VR uses best_contact_email, DSPD uses client_email
            let email = '';
            if (client.payer_type === 'VR') {
              email = client.best_contact_email || '';
            } else if (client.payer_type === 'DSPD') {
              email = client.client_email || '';
            }

            // Phone logic: VR uses best_contact_phone, DSPD uses contacts array
            let phone = '';
            if (client.payer_type === 'VR') {
              phone = client.best_contact_phone || '';
            } else if (client.payer_type === 'DSPD') {
              // Look for mobile phone first, then home phone in contacts
              if (client.contacts && client.contacts.length > 0) {
                // First try to find mobile phone
                const mobileContact = client.contacts.find(
                  c => c.phone_type && c.phone_type.toLowerCase() === 'mobile' && c.phone
                );
                if (mobileContact) {
                  phone = mobileContact.phone;
                } else {
                  // Try alternate phone if it's mobile
                  const mobileAltContact = client.contacts.find(
                    c => c.phone_alt_type && c.phone_alt_type.toLowerCase() === 'mobile' && c.phone_alt
                  );
                  if (mobileAltContact) {
                    phone = mobileAltContact.phone_alt;
                  } else {
                    // Fall back to home phone
                    const homeContact = client.contacts.find(
                      c => c.phone_type && c.phone_type.toLowerCase() === 'home' && c.phone
                    );
                    if (homeContact) {
                      phone = homeContact.phone;
                    } else {
                      // Try alternate phone if it's home
                      const homeAltContact = client.contacts.find(
                        c => c.phone_alt_type && c.phone_alt_type.toLowerCase() === 'home' && c.phone_alt
                      );
                      if (homeAltContact) {
                        phone = homeAltContact.phone_alt;
                      }
                    }
                  }
                }
              }
            }

            return {
              ...client,
              email,
              phone,
            };
          });
        })
        .catch((error) => {
          this.$notifyError("Could not load clients: " + error);
        });
    },
    toggleClientStatus(client) {
      const isActive = client.active === 1;
      const newStatus = isActive ? 0 : 1;
      const action = isActive ? "deactivated" : "activated";

      return clientsService
        .update(client.id, { active: newStatus })
        .then(() => {
          this.$notifySuccess(`Successfully ${action} client!`);
          this.fetchClients(this.pagination.currentPage);
        })
        .catch((error) => {
          this.$notifyError(`Error ${action.slice(0, -1)}ing client: ${error}`);
        });
    },
    async saveClient() {
      const clientObj = { ...this.client };

      if (!clientObj.first_name) {
        this.$notifyError("No client first name!");
        return;
      }
      if (!clientObj.last_name) {
        this.$notifyError("No client last name!");
        return;
      }
      if (!clientObj.payer_type) {
        this.$notifyError("Please select a payer type!");
        return;
      }

      this.$spinner.show();

      const { schedule, ...clientDataWithoutSchedule } = clientObj;
      clientsService
        .create(clientDataWithoutSchedule)
        .then(async (newClient) => {
          this.$notifySuccess("Successfully created client.");
          this.$modal.close("create_or_edit_client");

          // Navigate to ClientDetails with Details tab selected
          const clientName = `${newClient.first_name} ${newClient.last_name}`;
          this.$router.push({
            name: "ClientDetails",
            params: { id: newClient.id },
            query: { name: clientName, tab: 'details' },
          });
        })
        .catch((error) => {
          this.$notifyError("Error: " + error);
        })
        .finally(() => {
          this.$spinner.hide();
        });
    },
    createClient(authorizationData = null) {
      this.newClient = {
        first_name: "",
        last_name: "",
        payer_type: "DSPD",
      };

      if (authorizationData) {
        const parsedName = this.parseClientName(
          authorizationData.client_name || "",
        );
        this.newClient.first_name = parsedName.first_name;
        this.newClient.last_name = parsedName.last_name;
      }

      this.$modal.open("create_or_edit_client");
    },
    parseClientName(fullName) {
      if (!fullName || typeof fullName !== "string")
        return { first_name: "", last_name: "" };

      const trimmedName = fullName.trim();

      if (trimmedName.includes(",")) {
        const parts = trimmedName.split(",").map((part) => part.trim());
        if (parts.length >= 2) {
          return {
            first_name: parts[1] || "",
            last_name: parts[0] || "",
          };
        }
      }

      const nameParts = trimmedName
        .split(" ")
        .filter((part) => part.length > 0);
      if (nameParts.length >= 2) {
        return {
          first_name: nameParts[0],
          last_name: nameParts.slice(1).join(" "),
        };
      }

      return {
        first_name: "",
        last_name: trimmedName,
      };
    },
    handleRowClick(client) {
      const clientName = `${client.first_name} ${client.last_name}`;
      this.$router.push({
        name: "ClientDetails",
        params: { id: client.id },
        query: { name: clientName },
      });
    },
  },
};
</script>

<style scoped>
/* Client Modal Form Styling */

/* Create Client Modal Styles */
.client-form-container {
  max-height: calc(85vh - 180px);
  overflow-y: auto;
  padding: 20px;
}

/* Dialog body overflow handling for create_or_edit_client modal */
:deep(.el-dialog__body) {
  overflow: hidden !important;
  padding: 0 !important;
}

/* Compact filter section */
.filter-section {
  margin-bottom: 1.5rem;
}

.filter-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0;
}

.filter-group {
  display: flex;
  align-items: center;
  min-width: 0;
  flex-shrink: 0;
}

.filter-group.search-group {
  flex: 1;
  max-width: 400px;
}

.search-input {
  width: 100%;
}

.compact-filter {
  min-width: 150px;
  width: 150px;
}

@media (max-width: 768px) {
  .filter-row {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .filter-group.search-group {
    max-width: unset;
  }

  .compact-filter {
    width: 100%;
    min-width: unset;
  }
}
</style>
